// This file adapts MIT code from https://github.com/react-spring/zustand/blob/master/src/middleware.ts
/* eslint-disable @typescript-eslint/no-explicit-any */
import { StateCreatorNamed } from './Store';
import { PartialState, StateCreator } from 'zustand';

/**
 * DON'T USE THIS DIRECTLY - use {@link createStore} instead.
 *
 * This function integrates zustand stores with the redux devtools browser extension.
 * See the documentation for zustand/middleware for details.
 *
 * I only fixed the typings and changed one thing:
 * The call to extension.connect passes the given `prefix` as redux store name, making it easier to find the correct store.
 *
 * @author Bennett Piater
 */
export const devtools = <T>(
  fn: StateCreatorNamed<T>,
  prefix?: string
): StateCreator<T> => (set, get, api): T => {
  let extension;
  try {
    extension =
      (window as any).__REDUX_DEVTOOLS_EXTENSION__ ||
      (window as any).top.__REDUX_DEVTOOLS_EXTENSION__;
  } catch {}

  if (!extension) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Please install/enable Redux devtools extension');
    }
    (api as any).devtools = null;
    return fn(set, get, api);
  }
  const namedSet = (state: PartialState<T>, name?: string | boolean): void => {
    set(state);
    if (name) {
      (api as any).devtools.send((api as any).devtools.prefix + name, get());
    }
  };
  const initialState = fn(namedSet, get, api);
  if (!(api as any).devtools) {
    // NOTE: this is my only semantic change.
    // This sets the redux store name to something more readable.
    (api as any).devtools = extension.connect({
      name: `${prefix} (${window.location.pathname})`
    });
    (api as any).devtools.prefix = prefix ? `${prefix} > ` : '';
    (api as any).devtools.subscribe((message: any) => {
      if (message.type === 'DISPATCH' && message.state) {
        const ignoreState =
          message.payload.type === 'JUMP_TO_ACTION' ||
          message.payload.type === 'JUMP_TO_STATE';
        namedSet(
          JSON.parse(message.state),
          !(initialState as any).dispatch && !ignoreState && 'setState'
        );
      }
    });
    (api as any).devtools.init(initialState);
  }
  return initialState;
};
